import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useAppCtx } from "../contexts/AppContext";
import { PeriodFilter, periodParams } from "../components/PeriodFilter";
import { PartnerAutocomplete } from "../components/PartnerAutocomplete";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function CreditNotesPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("credit_notes", "write");
    const { entities, currencies, selectedEntity, selectedSegment } = useAppCtx();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blank());
    const [period, setPeriod] = useState({ from: null, to: null, preset: "this_month" });

    function blank() {
        return {
            entity_id: "", segment: "Voice", cn_number: "", date: today(),
            partner_name: "", currency: "USD", amount: "", fx_rate: "", remarks: "",
        };
    }

    const load = async () => {
        const params = { ...periodParams(period) };
        if (selectedEntity !== "all") params.entity_id = selectedEntity;
        if (selectedSegment !== "all") params.segment = selectedSegment;
        const { data } = await api.get("/credit-notes", { params });
        setRows(data);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [selectedEntity, selectedSegment, period]);

    const submit = async () => {
        if (!form.entity_id || !form.partner_name || !form.amount || !form.remarks?.trim()) {
            toast.error("Entity, partner, amount and remarks are required");
            return;
        }
        try {
            await api.post("/credit-notes", {
                ...form,
                amount: parseFloat(form.amount),
                fx_rate: form.fx_rate ? parseFloat(form.fx_rate) : null,
                cn_number: form.cn_number || null,
            });
            toast.success("Credit note created");
            setOpen(false);
            setForm(blank());
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete credit note?")) return;
        await api.delete(`/credit-notes/${id}`);
        await load();
    };

    const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

    return (
        <div data-testid="credit-notes-page">
            <PageHeader
                title="Credit Notes"
                eyebrow="Vouchers"
                description="Auto-numbered voucher (editable). Remarks are mandatory."
                action={
                    canWrite ? (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f]" data-testid="cn-add-btn">
                                    + New credit note
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-xl" data-testid="cn-dialog">
                                <DialogHeader>
                                    <DialogTitle>New credit note</DialogTitle>
                                </DialogHeader>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Entity">
                                        <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="cn-entity-select"><SelectValue placeholder="Select entity"/></SelectTrigger>
                                            <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Segment">
                                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="cn-segment-select"><SelectValue/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Voice">Voice (USD base)</SelectItem>
                                                <SelectItem value="SMS">SMS (EUR base)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="CN Number (optional, auto)">
                                        <Input value={form.cn_number} onChange={(e) => setForm({ ...form, cn_number: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="cn-number-input"/>
                                    </Field>
                                    <Field label="Date">
                                        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="cn-date-input"/>
                                    </Field>
                                    <Field label="Partner">
                                        <PartnerAutocomplete
                                            value={form.partner_name}
                                            onChange={(v) => setForm({ ...form, partner_name: v })}
                                            placeholder="Type to search partner…"
                                            testId="cn-partner-input"
                                        />
                                    </Field>
                                    <Field label="Currency">
                                        <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                            <SelectTrigger className="rounded-sm" data-testid="cn-currency-select"><SelectValue/></SelectTrigger>
                                            <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Amount">
                                        <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="cn-amount-input"/>
                                    </Field>
                                    <Field label="FX rate override (optional)">
                                        <Input type="number" step="0.000001" value={form.fx_rate} onChange={(e) => setForm({ ...form, fx_rate: e.target.value })} className="rounded-sm h-9 font-mono" data-testid="cn-fx-input"/>
                                    </Field>
                                    <div className="col-span-2">
                                        <Field label="Remarks (mandatory)">
                                            <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={3} className="rounded-sm" data-testid="cn-remarks-input"/>
                                        </Field>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                                    <Button onClick={submit} className="rounded-sm bg-[#002FA7]" data-testid="cn-submit-btn">Create</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    ) : null
                }
            />

            <div className="flex flex-wrap items-center gap-3 mb-3">
                <PeriodFilter initialPreset="this_month" onChange={setPeriod}/>
            </div>

            <div className="bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-[13px] financial min-w-[900px]">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-3 py-2.5">Date</th>
                            <th className="px-3 py-2.5">CN #</th>
                            <th className="px-3 py-2.5">Entity</th>
                            <th className="px-3 py-2.5">Segment</th>
                            <th className="px-3 py-2.5">Partner</th>
                            <th className="px-3 py-2.5 num">Amount</th>
                            <th className="px-3 py-2.5">Cur</th>
                            <th className="px-3 py-2.5 num">Base</th>
                            <th className="px-3 py-2.5">Remarks</th>
                            <th className="px-3 py-2.5 w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td colSpan={10} className="text-center text-slate-500 py-10">No credit notes</td></tr>}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100" data-testid={`cn-row-${r.id}`}>
                                <td className="px-3 py-2 font-mono">{r.date}</td>
                                <td className="px-3 py-2 font-mono">{r.cn_number}</td>
                                <td className="px-3 py-2">{entityName(r.entity_id)}</td>
                                <td className="px-3 py-2"><SegBadge segment={r.segment}/></td>
                                <td className="px-3 py-2">{r.partner_name}</td>
                                <td className="px-3 py-2 num">{Number(r.amount).toFixed(2)}</td>
                                <td className="px-3 py-2 font-mono">{r.currency}</td>
                                <td className="px-3 py-2 num font-semibold">{Number(r.amount_base).toFixed(2)} <span className="text-[10px] text-slate-400">{r.base_currency}</span></td>
                                <td className="px-3 py-2 text-[12px] text-slate-600 max-w-xs truncate">{r.remarks}</td>
                                <td className="px-3 py-2">
                                    {canWrite && <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-600" data-testid={`cn-delete-${r.id}`}><Trash2 size={13}/></Button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
